import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { WarehouseLocalizationCollectionItemResponse } from '../responses/warehouse-localization-collection-item.response';
import { WarehouseLocalizationResponse } from '../responses/warehouse-localization.response';
import { blank, Guid } from '@web/utils';
import { WarehouseLocalizationUpdateRequest } from '../requests/warehouse-localization-update.request';
import { WarehouseLocalizationCreateRequest } from '../requests/warehouse-localization-create.request';

@Injectable({
  providedIn: 'root',
})
export class WarehouseLocalizationApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(
    request: CollectionRequest,
    cacheable = false,
  ): Observable<
    CollectionResponse<WarehouseLocalizationCollectionItemResponse>
  > {
    const path = 'admin/dictionary/localisation';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            WarehouseLocalizationCollectionItemResponse,
          ),
        ),
      );
  }

  public update(
    id: Guid,
    request: WarehouseLocalizationUpdateRequest,
  ): Observable<void> {
    const path = `admin/dictionary/localisation/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      WarehouseLocalizationUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public create(request: WarehouseLocalizationCreateRequest) {
    const path = `admin/dictionary/localisation`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder.build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      WarehouseLocalizationCreateRequest,
    );

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public fetchDetailsById(id: Guid): Observable<WarehouseLocalizationResponse> {
    const path = `admin/dictionary/localisation/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            WarehouseLocalizationResponse,
          ),
        ),
      );
  }

  public remove(id: Guid): Observable<void> {
    const path = `admin/dictionary/localisation/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder.build();

    return this.provider.http.delete(url, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Warehouse;
  }
}
